import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { Avatar, RateBar, SkeletonHero, ErrorMessage, StubNote, MapThumb, usePageMeta } from '../components/ui';
import { pct } from '../data/meta';

function MapDetail() {
  const { name } = useParams();
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [threats, setThreats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  usePageMeta({
    title: m?.map ? `${m.map} Best Brawlers` : 'Maps',
    description: m?.map ? `Top Brawl Stars brawlers on ${m.map}, ranked by win rate from tracked battles.` : undefined,
  });

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await api.getMap(name);
        // Threats = brawlers that counter this map's strongest picks (real head-to-head).
        const picks = (data.top_brawlers || []).slice(0, 2);
        const pickIds = new Set(picks.map((p) => p.brawler_id));
        const mus = await Promise.all(
          picks.map((p) => api.getBrawlerMatchups(p.brawler_id, 5).catch(() => null))
        );
        const agg = {};
        picks.forEach((p, i) => {
          (mus[i]?.countered_by || []).forEach((c) => {
            if (pickIds.has(c.brawler_id)) return; // a recommended pick isn't a "threat"
            const beats = 100 - c.win_rate; // how often the counter beats this pick
            const cur = agg[c.brawler_id];
            if (!cur || beats > cur.beats) {
              agg[c.brawler_id] = { brawler_id: c.brawler_id, brawler_name: c.brawler_name, beats, vs: p.brawler_name };
            }
          });
        });
        const computed = Object.values(agg).sort((a, b) => b.beats - a.beats).slice(0, 4);
        if (!cancelled) {
          setM(data);
          setThreats(computed);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [name]);

  if (loading) return <div className="page"><div className="wrap"><SkeletonHero /></div></div>;
  if (error) return <div className="page"><div className="wrap"><ErrorMessage message={error} /></div></div>;
  if (!m) return <div className="page"><div className="wrap"><ErrorMessage message="Map not found" /></div></div>;

  const top = m.top_brawlers || [];
  const best = top[0];
  const draft = top.slice(0, 3);

  return (
    <div className="page">
      <div className="wrap">
        <span className="back" onClick={() => navigate('/maps')}>← Maps</span>

        <div className="map-hero">
          <MapThumb id={m.event_id} mode={m.mode} variant="map-shot" />
          <div className="map-meta">
            <div className="tags"><span className="pill-chip lime">{m.mode}</span></div>
            <h1>{m.map}</h1>
            <p className="map-desc">
              A {m.mode} map with {(m.total_battles ?? 0).toLocaleString()} tracked battles.
              Here's what's winning on it right now.
            </p>
            <div className="map-stats">
              <div className="stat"><div className="v">{best ? best.brawler_name : '—'}</div><div className="l">Top pick</div></div>
              <div className="stat"><div className="v">{best ? pct(best.win_rate) : '—'}</div><div className="l">Best win rate</div></div>
              <div className="stat"><div className="v">{(m.total_battles ?? 0).toLocaleString()}</div><div className="l">Battles</div></div>
            </div>
          </div>
        </div>

        <div className="bdcols">
          <div className="panel">
            <h3><span className="ic win">★</span>Best brawlers here</h3>
            {top.length > 0 ? (
              <table className="meta" style={{ marginTop: -4 }}>
                <tbody>
                  {top.map((b, i) => (
                    <tr key={b.brawler_id} onClick={() => navigate(`/brawlers/${b.brawler_id}`)}>
                      <td className="rank" style={{ width: 26 }}>{i + 1}</td>
                      <td>
                        <div className="bcell"><Avatar name={b.brawler_name} size={40} /><div className="bname">{b.brawler_name}</div></div>
                      </td>
                      <td style={{ width: 150 }}><RateBar value={b.win_rate} max={65} lime /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <StubNote>No brawler has enough battles on this map yet.</StubNote>
            )}
          </div>

          <div className="col-stack">
            <div className="panel">
              <h3><span className="ic win">✓</span>Suggested picks</h3>
              <p className="subhead" style={{ margin: '0 0 12px' }}>Highest win rates on this map</p>
              {draft.length > 0 ? (
                <>
                  <div className="mate-row" style={{ gap: 16 }}>
                    {draft.map((b) => (
                      <div key={b.brawler_id} className="mate" onClick={() => navigate(`/brawlers/${b.brawler_id}`)} style={{ cursor: 'pointer', width: 64 }}>
                        <Avatar name={b.brawler_name} size={50} /><span className="nm">{b.brawler_name}</span>
                      </div>
                    ))}
                  </div>
                  <div className="tip">
                    <b>Why it works:</b> {draft.map((b) => b.brawler_name).join(', ')} hold the highest
                    win rates here{best ? `, led by ${best.brawler_name} at ${pct(best.win_rate)}` : ''}.
                  </div>
                </>
              ) : (
                <StubNote>Not enough data to suggest a draft yet.</StubNote>
              )}
            </div>

            <div className="panel">
              <h3><span className="ic beat">⚔</span>Watch out for</h3>
              {threats.length > 0 ? (
                <>
                  <div className="mate-row" style={{ gap: 16 }}>
                    {threats.map((t) => (
                      <div key={t.brawler_id} className="mate" onClick={() => navigate(`/brawlers/${t.brawler_id}`)} style={{ cursor: 'pointer', width: 70 }}>
                        <Avatar name={t.brawler_name} size={50} />
                        <span className="nm">{t.brawler_name}</span>
                        <span className="trend down" style={{ fontSize: 12 }}>{t.beats.toFixed(0)}% vs {t.vs}</span>
                      </div>
                    ))}
                  </div>
                  <div className="tip beat">
                    These brawlers counter the top picks here — {threats[0].brawler_name} beats
                    {' '}{threats[0].vs} {threats[0].beats.toFixed(0)}% of the time. Draft around them.
                  </div>
                </>
              ) : (
                <StubNote>Not enough head-to-head data on this map's picks to flag threats yet.</StubNote>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MapDetail;
